import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import {
  Card,
  FormField,
  Input,
  Select,
  Textarea,
  Button,
  Eyebrow,
  Loader,
  Divider,
  Container,
} from '../ui';

const PersonalDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState('basic');

  // State for custom values when "Others" is selected
  const [customValues, setCustomValues] = useState({
    gender: '',
    kootam: '',
    kovil: ''
  });

  const [formData, setFormData] = useState({
    // Basic Details
    first_name: '',
    last_name: '',
    email: '',
    dob: '',
    gender: '',
    contact_no: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    // Advanced Details
    blood_group: '',
    alternate_contact_no: '',
    marital_status: '',
    work_phone: '',
    extension: '',
    mobile_no: '',
    preferred_contact: '',
    secondary_email: '',
    emergency_contact: '',
    emergency_phone: '',
    personal_website: '',
    best_time_to_contact: '',
    linkedin_profile: '',
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
    kootam: '',
    kovil: ''
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        setFetchLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          return;
        }
        const response = await fetch(`${baseurl}/api/member/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.success && data.data) {
          const memberData = data.data;
          // Store member ID for update
          localStorage.setItem('memberId', memberData.mid);
          // Prepare form data
          const newFormData = {
            // Basic Details
            first_name: memberData.first_name || '',
            last_name: memberData.last_name || '',
            email: memberData.email || '',
            dob: memberData.dob || '',
            gender: memberData.gender || '',
            contact_no: memberData.contact_no || '',
            address: memberData.address || '',
            city: memberData.city || '',
            state: memberData.state || '',
            zip_code: memberData.zip_code || '',
            // Advanced Details
            blood_group: memberData.blood_group || '',
            alternate_contact_no: memberData.alternate_contact_no || '',
            marital_status: memberData.marital_status || '',
            work_phone: memberData.work_phone || '',
            extension: memberData.extension || '',
            mobile_no: memberData.mobile_no || '',
            preferred_contact: memberData.preferred_contact || '',
            secondary_email: memberData.secondary_email || '',
            emergency_contact: memberData.emergency_contact || '',
            emergency_phone: memberData.emergency_phone || '',
            personal_website: memberData.personal_website || '',
            best_time_to_contact: memberData.best_time_to_contact || '',
            linkedin_profile: memberData.linkedin_profile || '',
            facebook: memberData.facebook || '',
            instagram: memberData.instagram || '',
            twitter: memberData.twitter || '',
            youtube: memberData.youtube || '',
            kootam: memberData.kootam || '',
            kovil: memberData.kovil || ''
          };

          // Initialize custom values if existing value is not in predefined options
          const genderOptions = ['male', 'female', 'Others'];
          const kootamOptions = ['Agamudayar', 'Karkathar', 'Kallar', 'Maravar', 'Servai', 'Others'];
          const kovilOptions = ['Madurai Meenakshi Amman', 'Thanjavur Brihadeeswarar', 'Palani Murugan', 'Srirangam Ranganathar', 'Kanchipuram Kamakshi Amman', 'Others'];

          setCustomValues({
            gender: !genderOptions.includes(memberData.gender) ? memberData.gender : '',
            kootam: !kootamOptions.includes(memberData.kootam) ? memberData.kootam : '',
            kovil: !kovilOptions.includes(memberData.kovil) ? memberData.kovil : ''
          });

          setFormData(newFormData);
        } else {
          setError(data.message || 'Failed to load member data');
        }
      } catch (err) {
        console.error('Error fetching member data:', err);
        setError(`Failed to load member data: ${err.message}`);
      } finally {
        setFetchLoading(false);
      }
    };
    if (id) {
      fetchMemberData();
    } else {
      setFetchLoading(false);
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomInputChange = (e) => {
    const { name, value } = e.target;
    setCustomValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Also update the main form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const memberId = localStorage.getItem('memberId');
      if (!token) {
        setError('Authentication required');
        return;
      }
      if (!memberId) {
        setError('Member ID not found');
        return;
      }

      // Prepare form data with custom values
      const formDataToSend = { ...formData };

      // If custom values exist, use them instead of "Others"
      if (customValues.gender && formData.gender === 'Others') {
        formDataToSend.gender = customValues.gender;
      }
      if (customValues.kootam && formData.kootam === 'Others') {
        formDataToSend.kootam = customValues.kootam;
      }
      if (customValues.kovil && formData.kovil === 'Others') {
        formDataToSend.kovil = customValues.kovil;
      }

      const formDataObj = new FormData();
      // Append all form data
      Object.entries(formDataToSend).forEach(([key, value]) => {
        formDataObj.append(key, value);
      });

      const response = await fetch(`${baseurl}/api/member/update/${memberId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formDataObj
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success !== false) {
        setSuccess('Profile updated successfully');
        setTimeout(() => navigate(-1), 2000);
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(`Failed to update profile: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (fieldName, config) => {
    if (config.type === 'select') {
      // Special handling for gender, kootam, and kovil with "Others" option
      if (['gender', 'kootam', 'kovil'].includes(fieldName)) {
        const isOthersSelected = formData[fieldName] === 'Others';
        const hasCustomValue = customValues[fieldName] && !isOthersSelected;

        return (
          <div key={fieldName} className="space-y-3">
            <FormField label={config.label}>
              <Select
                value={hasCustomValue ? 'Others' : (formData[fieldName] || '')}
                name={fieldName}
                onChange={handleInputChange}
              >
                <option value="" disabled>{config.label}</option>
                {config.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormField>

            {(isOthersSelected || hasCustomValue) && (
              <FormField label={`Custom ${config.label}`}>
                <Input
                  name={fieldName}
                  value={customValues[fieldName]}
                  onChange={handleCustomInputChange}
                />
              </FormField>
            )}
          </div>
        );
      }

      // Regular select field
      return (
        <FormField key={fieldName} label={config.label}>
          <Select
            value={formData[fieldName]}
            name={fieldName}
            onChange={handleInputChange}
          >
            <option value="" disabled>{config.label}</option>
            {config.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormField>
      );
    }

    // Regular text field
    return (
      <FormField key={fieldName} label={config.label} required={config.required}>
        {config.multiline ? (
          <Textarea
            name={fieldName}
            value={formData[fieldName]}
            onChange={handleInputChange}
            required={config.required}
            rows={config.rows}
          />
        ) : (
          <Input
            name={fieldName}
            type={config.type}
            value={formData[fieldName]}
            onChange={handleInputChange}
            required={config.required}
          />
        )}
      </FormField>
    );
  };

  if (fetchLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Loader label="Loading…" />
      </div>
    );
  }

  // Field configurations
  const basicFieldConfig = {
    first_name: { label: t('Name'), type: 'text', required: true },
    email: { label: t('email'), type: 'email', required: true },
    dob: { label: t('dob'), type: 'date', InputLabelProps: { shrink: true } },
    gender: {
      label: t('gender'),
      type: 'select',
      options: [
        { value: 'male', label: t('Male') },
        { value: 'female', label: t('Female') },
        // { value: 'other', label: t('Other') },
        { value: 'Others', label: t('Others') }
      ]
    },
    contact_no: { label: t('contactno'), type: 'text', required: true },
    address: { label: t('address'), type: 'text', multiline: true, rows: 3, required: true },
    city: { label: t('city'), type: 'text', required: true },
    state: { label: t('state'), type: 'text', required: true },
    zip_code: { label: t('Pin Code'), type: 'number', required: true }
  };

  const advancedFieldConfig = {
    last_name: { label: t('lastname'), type: 'text' },
    blood_group: { label: t('bloodgroup'), type: 'text' },
    marital_status: {
      label: t('maritalstatus'),
      type: 'select',
      options: [
        { value: 'single', label: t('Single') },
        { value: 'married', label: t('Married') },
        { value: 'divorced', label: t('Divorced') },
        { value: 'widowed', label: t('Widowed') }
      ]
    },
    mobile_no: { label: t('mobileno'), type: 'text' },
    preferred_contact: { label: t('preferredcontact'), type: 'text' },
    secondary_email: { label: t('secondaryemail'), type: 'email' },
    emergency_contact: { label: t('emergencycontact'), type: 'text' },
    personal_website: { label: t('personalwebsite'), type: 'text' },
    best_time_to_contact: {
      label: t('besttimetocontact'),
      type: 'select',
      options: [
        { value: 'morning', label: t('Morning') },
        { value: 'afternoon', label: t('Afternoon') },
        { value: 'evening', label: t('Evening') },
        { value: 'weekend', label: t('Weekend') }
      ]
    },
    linkedin_profile: { label: t('LinkedIn Profile'), type: 'text' },
    facebook: { label: t('Facebook'), type: 'text' },
    instagram: { label: t('Instagram'), type: 'text' },
    twitter: { label: t('Twitter'), type: 'text' },
    youtube: { label: t('YouTube'), type: 'text' },
    kootam: {
      label: t('Kootam'),
      type: 'select',
      options: [
        { value: 'Agamudayar', label: t('Agamudayar') },
        { value: 'Karkathar', label: t('Karkathar') },
        { value: 'Kallar', label: t('Kallar') },
        { value: 'Maravar', label: t('Maravar') },
        { value: 'Servai', label: t('Servai') },
        { value: 'Others', label: t('Others') }
      ]
    },
    kovil: {
      label: t('Kovil'),
      type: 'select',
      options: [
        { value: 'Madurai Meenakshi Amman', label: t('Madurai Meenakshi Amman') },
        { value: 'Thanjavur Brihadeeswarar', label: t('Thanjavur Brihadeeswarar') },
        { value: 'Palani Murugan', label: t('Palani Murugan') },
        { value: 'Srirangam Ranganathar', label: t('Srirangam Ranganathar') },
        { value: 'Kanchipuram Kamakshi Amman', label: t('Kanchipuram Kamakshi Amman') },
        { value: 'Others', label: t('Others') }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-bg pb-20 text-text">
      <Header />
      {/* Dark hero band */}
      <div className="on-dark bg-navy relative px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full text-ink ring-1 ring-inset ring-line/20 transition-colors hover:bg-primary/10"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Eyebrow className="block text-center">Profile</Eyebrow>
        <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink text-center">
          {t('personalDetails')}
        </h1>
      </div>

      <Container width="narrow" className="mt-8 max-w-[620px]">
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2 rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 rounded-md border border-success/20 bg-success/10 px-4 py-3 text-sm font-medium text-success">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {success}
            </div>
          )}

          {/* Basic Details Accordion */}
          <Card padding="none">
            <details
              open={expanded === 'basic'}
              onToggle={(e) => handleAccordionChange('basic')(e, e.currentTarget.open)}
              className="group"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between px-6 py-5 [&::-webkit-details-marker]:hidden">
                <span className="font-display text-lg font-semibold tracking-tight text-ink">
                  Basic <em className="kbc-serif">Details</em>
                </span>
                <ChevronDown className="h-5 w-5 text-muted transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <div className="px-6 pb-6">
                <Divider className="mb-5" />
                <p className="mb-4 text-sm text-muted">
                  Essential personal information
                </p>
                <div className="space-y-4">
                  {Object.entries(basicFieldConfig).map(([fieldName, config]) =>
                    renderField(fieldName, config)
                  )}
                </div>
              </div>
            </details>
          </Card>

          {/* Advanced Details Accordion */}
          <Card padding="none">
            <details
              open={expanded === 'advanced'}
              onToggle={(e) => handleAccordionChange('advanced')(e, e.currentTarget.open)}
              className="group"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between px-6 py-5 [&::-webkit-details-marker]:hidden">
                <span className="font-display text-lg font-semibold tracking-tight text-ink">
                  Advanced <em className="kbc-serif">Details</em>
                </span>
                <ChevronDown className="h-5 w-5 text-muted transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <div className="px-6 pb-6">
                <Divider className="mb-5" />
                <p className="mb-4 text-sm text-muted">
                  Additional contact information and preferences
                </p>
                <div className="space-y-4">
                  {Object.entries(advancedFieldConfig).map(([fieldName, config]) =>
                    renderField(fieldName, config)
                  )}
                </div>
              </div>
            </details>
          </Card>

          <Button type="submit" fullWidth size="lg" loading={loading} disabled={loading}>
            {loading ? 'Updating…' : t('updateChanges')}
          </Button>
        </form>
      </Container>
      <Footer />
    </div>
  );
};

export default PersonalDetailsPage;
